import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect, useMemo } from "react";
import Particles from "react-tsparticles";
import { loadFull } from "tsparticles";

// Local slides from public folder
const slides = [
  { img: "/header_01.png", particlesColor: "#00fff7", particlesSpeed: 0.7 },
  { img: "/header_02.png", particlesColor: "#ff9f1a", particlesSpeed: 0.8 },
  { img: "/header_03.png", particlesColor: "#7f5af0", particlesSpeed: 0.6 },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);

  // Slide change every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => setCurrent(prev => (prev + 1) % slides.length), 5000);
    return () => clearInterval(interval);
  }, []);

  const particlesInit = async (main) => await loadFull(main);

  const particlesOptions = useMemo(() => ({
    fullScreen: { enable: false },
    particles: {
      number: { value: 50, density: { enable: true, area: 800 } },
      color: { value: slides[current].particlesColor },
      shape: { type: ["circle", "triangle", "polygon"], polygon: { sides: 6 } },
      opacity: { value: 0.25, random: true },
      size: { value: { min: 1, max: 3 }, random: true, anim: { enable: true, speed: 3, size_min: 0.5, sync: false } },
      move: { enable: true, speed: slides[current].particlesSpeed, direction: "none", random: true, outMode: "bounce" },
    },
    interactivity: { events: { onHover: { enable: true, mode: "repulse" } } },
  }), [current]);

  const slideVariants = {
    enter: { x: "100%", opacity: 0, scale: 1.05 },
    center: { x: "0%", opacity: 1, scale: 1 },
    exit: { x: "-100%", opacity: 0, scale: 1.05 },
  };

  return (
    <section className="relative h-screen flex flex-col md:flex-row items-center justify-center overflow-hidden bg-gradient-to-tr from-[#0f111a] via-[#1a1f2b] to-[#0f111a] text-white px-6 md:px-16">

      {/* Particles */}
      <Particles id="tsparticles" init={particlesInit} options={particlesOptions} className="absolute inset-0" />

      {/* Left: Slideshow */}
      <div className="relative w-full md:w-1/2 h-[70vh] md:h-[90vh] flex items-center justify-center rounded-2xl shadow-2xl overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.img
            key={current}
            src={slides[current].img}
            alt={`slide-${current}`}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="absolute w-full h-full object-cover rounded-2xl"
            style={{ willChange: "transform, opacity" }}
          />
        </AnimatePresence>

        {/* Overlay for subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0f111a]/10 via-[#1a1f2b]/10 to-[#0f111a]/10 pointer-events-none rounded-2xl"></div>

        {/* Animated dots */}
        <div className="absolute top-12 left-5 w-3 h-3 bg-[#00fff7] rounded-full animate-bounce-slow opacity-70"></div>
        <div className="absolute bottom-16 right-12 w-5 h-5 border-2 border-[#ff9f1a] rounded-full animate-pulse-slow opacity-40"></div>
      </div>

      {/* Right: Content */}
      <div className="relative w-full md:w-1/2 text-center md:text-left mt-10 md:mt-0 md:pl-12 z-10">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
          className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight bg-clip-text text-transparent bg-gradient-to-r from-[#00fff7] to-[#0077ff] animate-text-glow"
        >
          Powering the Future of IT Infrastructure
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 1.2 }}
          className="text-lg md:text-xl text-gray-300 mb-10"
        >
          Basore Technologies delivers enterprise-grade IT solutions — secure, automated, and intelligent.
          We simplify your IT, so you can focus on innovation.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 1.2 }}
          className="flex justify-center md:justify-start gap-5 flex-wrap"
        >
          <a href="#services" className="relative group overflow-hidden bg-gradient-to-r from-[#00fff7] to-[#0077ff] text-black font-semibold py-3 px-8 rounded-full shadow-xl transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_25px_rgba(0,255,247,0.6)]">
            <span className="relative z-10">Explore Services</span>
          </a>
          <a href="#contact" className="relative group border border-white/70 hover:border-[#00fff7] hover:text-[#00fff7] font-semibold py-3 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-[0_0_25px_rgba(0,255,247,0.5)]">
            <span className="relative z-10">Contact Us</span>
          </a>
        </motion.div>
      </div>

      {/* Animations */}
      <style jsx>{`
        @keyframes text-glow { 0%,100% { text-shadow: 0 0 5px #00fff7, 0 0 15px #0077ff; } 50% { text-shadow: 0 0 20px #00fff7, 0 0 40px #0077ff; } }
        .animate-text-glow { animation: text-glow 4s ease-in-out infinite; }
        @keyframes bounce-slow { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        @keyframes pulse-slow { 0%,100% { opacity: 0.4; } 50% { opacity: 1; } }
        .animate-bounce-slow { animation: bounce-slow 5s ease-in-out infinite; }
        .animate-pulse-slow { animation: pulse-slow 6s ease-in-out infinite; }
      `}</style>
    </section>
  );
}
